import React, { useEffect, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon, PhoneIcon, MailIcon, UserIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
// Fix for default marker icons in React Leaflet
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Custom icon for the Embassy (red/gold to stand out)
const embassyIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Component to fit map bounds to all markers
function FitBounds({
  consulates
}: {
  consulates: ConsulateData[];
}) {
  const map = useMap();
  useEffect(() => {
    if (consulates.length > 0) {
      const bounds = L.latLngBounds(consulates.map(c => [c.lat, c.lng] as [number, number]));
      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 5
      });
    }
  }, [map, consulates]);
  return null;
}
export interface ConsulateData {
  name: string;
  nameEs?: string;
  address: string;
  phone: string;
  email?: string;
  consulGeneral?: string;
  isEmbassy?: boolean;
  facebook?: string;
  lat: number;
  lng: number;
}
interface ConsulateMapProps {
  consulates: ConsulateData[];
  accentColor: string;
  expanded?: boolean;
}
export function ConsulateMap({
  consulates,
  accentColor,
  expanded = false
}: ConsulateMapProps) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <div className="w-full relative z-0" style={{
    height: expanded ? '65vh' : '280px'
  }} data-id="element-2198" data-mp="12xkavmxtno47">
      <MapContainer center={[39.0, -98.0]} zoom={4} scrollWheelZoom={expanded} className="w-full h-full" data-id="element-2199" data-mp="xyhbai0m2a3m">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-2200" data-mp="17r6cmk2g67l8" />
        <FitBounds consulates={consulates} data-id="element-2201" data-mp="f2r9ausohdpq" />
        {consulates.map((consulate, index) => <Marker key={index} position={[consulate.lat, consulate.lng]} icon={consulate.isEmbassy ? embassyIcon : defaultIcon} data-id="element-2202" data-mp="t20fd1n66rfz" data-mpi={index}>
            <Popup className="font-nunito" data-id="element-2203" data-mp="24fup70ve92n9">
              <div className="flex flex-col gap-1.5 min-w-[200px]" data-id="element-2204" data-mp="24ekrwdwd7yho">
                <h4 className={`font-bold text-sm m-0 ${accentColor}`} data-id="element-2205" data-mp="20dr3zkixajos">
                  {isEs && consulate.nameEs ? consulate.nameEs : consulate.name}
                </h4>

                <div className="flex items-start gap-1.5 mt-1" data-id="element-2206" data-mp="2cxxstil20ybn">
                  <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-2207" data-mp="2cvbmtndtaqvj" />
                  <span className="text-xs text-gray-600 leading-tight m-0" data-id="element-2208" data-mp="1y1yqkokmlsef">
                    {consulate.address}
                  </span>
                </div>

                {consulate.consulGeneral && <div className="flex items-start gap-1.5" data-id="element-2209" data-mp="2qr5kfa94xttc">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-2210" data-mp="2vmkxw138dnp8" />
                    <span className="text-xs text-gray-600 leading-tight m-0" data-id="element-2211" data-mp="2gxykw612t816">
                      <span className="font-semibold text-gray-700" data-id="element-2212" data-mp="2lxkt2121ry34">
                        {isEs ? 'Cónsul General: ' : 'Consul General: '}
                      </span>
                      {consulate.consulGeneral}
                    </span>
                  </div>}

                {consulate.phone && consulate.phone.split(',').map((phoneNum, idx) => <div key={idx} className="flex items-center gap-1.5" data-id="element-2213" data-mp="1lqg05cpp5k5k" data-mpi={idx}>
                      <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2214" data-mp="1grwe7cb3s8d7" />
                      <a href={`tel:${phoneNum.trim().replace(/-/g, '')}`} className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-2215" data-mp="1gtz4zyk2y5iz">
                        {phoneNum.trim()}
                      </a>
                    </div>)}

                {consulate.hours && <div className="flex items-start gap-1.5" data-id="element-2216" data-mp="1zpfnmh1h3t05">
                    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-2217" data-mp="1us75gggbroyw">
                      <circle cx="12" cy="12" r="10" data-id="element-2218" data-mp="2vp8qnrlwvrjd" />
                      <polyline points="12 6 12 12 16 14" data-id="element-2219" data-mp="2vqjutsj9r1b0" />
                    </svg>
                    <div className="flex flex-col gap-0.5" data-id="element-2220" data-mp="2zrc3ajkbh268">
                      <span className="text-xs text-gray-600 leading-tight m-0" data-id="element-2221" data-mp="34px3wk3hdnv7">
                        {consulate.hours}
                      </span>
                      <span className="text-xs text-gray-500 italic leading-tight m-0" data-id="element-2222" data-mp="39qbea8sk19we">
                        *Hours may vary on U.S. and Honduras holidays
                      </span>
                    </div>
                  </div>}

                {consulate.email && <div className="flex items-center gap-1.5" data-id="element-2223" data-mp="3ngoneeh6munb">
                    <MailIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2224" data-mp="3sfrf6x85j4dc" />
                    <a href={`mailto:${consulate.email}`} className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-2225" data-mp="196v5xgikwpt">
                      {consulate.email}
                    </a>
                  </div>}

                {consulate.facebook && <div className="flex items-center gap-1.5 mt-1 pt-1.5 border-t border-gray-100" data-id="element-2226" data-mp="37ri4u2ag5lgp">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 fill-current" aria-hidden="true" data-id="element-2227" data-mp="3mfv3x84tzkff">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-2228" data-mp="3vdx27fx6jyz0" />
                    </svg>
                    <a href={consulate.facebook} target="_blank" rel="noopener noreferrer" className={`text-xs font-semibold m-0 ${accentColor} hover:underline`} data-id="element-2229" data-mp="2azekl3q5ojna">
                      Facebook
                    </a>
                  </div>}
              </div>
            </Popup>
          </Marker>)}
      </MapContainer>
    </div>;
}